/** `/notifications` — in-app activity (spec §2.8). Deep-links to tiles, never to coordinates. */
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { NotificationItem } from '@drawearth/shared';
import { Icon, TopBar } from '../components/Widgets.tsx';
import { api } from '../lib/api.ts';
import { refreshUnread } from '../lib/session.ts';
import { useApp } from '../lib/store.ts';

function when(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.round(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function Notifications() {
  const user = useApp((s) => s.user);
  const authReady = useApp((s) => s.authReady);
  const [items, setItems] = useState<NotificationItem[] | null>(null);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    void api
      .notifications()
      .then(async ({ notifications }) => {
        if (!alive) return;
        setItems(notifications);
        if (notifications.some((n) => n.readAt === null)) {
          await api.markNotificationsRead();
          await refreshUnread();
        }
      })
      .catch(() => setItems([]));
    return () => {
      alive = false;
    };
  }, [user]);
  if (!authReady) return <div className="page page--paper" />;
  if (!user) return <Navigate to="/signin?next=/notifications" replace />;
  return (
    <div className="page page--paper">
      <TopBar />
      <div className="sheet">
        <h1>Activity</h1>
        {items === null && <div className="empty">Loading…</div>}
        {items && items.length === 0 && <div className="empty">Nothing yet. When someone paints next to you, or over you, it shows up here.</div>}
        <ul className="notifications" data-testid="notifications">
          {items?.map((n, i) => (
            <li key={n.id} className={n.readAt ? '' : 'is-unread'} style={{ animationDelay: `${i * 30}ms` }}>
              <strong>{n.title}</strong>
              <p>{n.body}</p>
              <span className="when">{when(n.createdAt)}</span>
              {n.link && (
                <Link to={n.link} className="btn btn--paper btn--icon" aria-label="Open">
                  <Icon name="map" />
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
